import { ComponentProps, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowBackIcon, ArrowForwardIcon, Box, HStack, VStack } from 'native-base';
import FeatherIcons from 'react-native-vector-icons/Feather';
import { get, includes, range } from 'lodash';
import moment from 'moment';

import { Button, ButtonGroup, CheckDone01Icon, Text } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { useAppSelector } from '@app/store/store';
import { selectUserProfile } from '@app/store/selectors';
import { formatToISOString } from '@app/utils/date-formatter';
import { DateTimePopover, useSiteConfig, useSiteTime } from '@app/features/store-core';
import { StoreReports } from '@app/features/store-inspections/components/store-reports';
import { useSimpleSite } from '@app/features/store-inspections/context/simple-site.context';
import { WeekFocus } from '@app/features/store-inspections/hooks/combine-store-reports.hook';
import { CalendarPicker, ResourceLoader } from '@app/components/shared';
import {
  ReviewVerifyReportsConsumer,
  ReviewVerifyReportsProvider,
} from '@app/features/store-inspections/context/review-verify-reports.context';
import { ShieldTickIcon } from '@app/features/flow-chart/icons';
import { PopoverTooltipStoreReports } from '@app/features/store-inspections/components/popover-tooltip-store-reports';
import { useAuthPermissions } from '@app/features/app';

type FormUtilizationPanelProps = {};

export const FormUtilizationPanel = ({}: FormUtilizationPanelProps) => {
  const { t } = useTranslation();
  const { locale, translate } = useAppLocale();
  const profile = useAppSelector(selectUserProfile);
  const siteTime = useSiteTime();
  const { siteConfig } = useSiteConfig();
  const permissions = useAuthPermissions(state => state.permissions);

  const [week, setWeek] = useState<WeekFocus | undefined>(undefined);
  const [moduleType, setModuleType] = useState<number>();
  const [tabItems] = useState<Partial<ComponentProps<typeof StoreReports>>[]>(() => {
    const sections = siteConfig?.companyInfo?.brand?.navSections?.find(_ => _.appType?.toLowerCase() === 'tablet')?.sections ?? [];
    const modules = sections.flatMap(_ => _.modules ?? []);
    const tabItems = [
      { identifier: 'fslForms', module: 1, hasConfig: 'inspectionConfig.fslFormsEnabled', icon: ShieldTickIcon },
      { identifier: 'checklistForms', module: 2, hasConfig: 'inspectionConfig.checklistFormsEnabled', icon: CheckDone01Icon },
    ].map(_item => {
      const module = modules.find(_ => _.identifier === _item.identifier);
      return { ..._item, title: module?.title, titleTranslations: module?.titleTranslations };
    });
    return tabItems.filter(_ => !!_.title && _.hasConfig && get(siteConfig?.config, _.hasConfig) === true);
  });
  const [download, setDownload] = useState<(() => void) | undefined>();
  const [generalManager, setGeneralManager] = useState<{ userId?: string; name?: string } | undefined>();
  const [{ max, min, minAllowedEdit }, setCalendarProps] = useState<
    Pick<ComponentProps<typeof CalendarPicker>, 'min' | 'max'> & { minAllowedEdit?: Date }
  >({
    minAllowedEdit: undefined,
    max: undefined,
    min: undefined,
  });

  // TODO: need to revisit
  const isAuthorized = !!permissions?.fslFormsReviewEod?.write;

  const isCurrentWeek = !max || !week?.endDate || moment(max).isSame(week?.endDate, 'date');
  const isEditEnabled = !!minAllowedEdit && !!week?.startDate && week.startDate >= minAllowedEdit;
  const tabFocus = tabItems.find(_ => _.module === moduleType) ?? tabItems[0];

  const handleSelectWeek = date => {
    const startEndOfWeek = siteTime.toStartEndOfWeekPart(date);
    setWeek({
      weekNumber: startEndOfWeek.weekNumber,
      startDate: startEndOfWeek.startTime,
      endDate: moment(startEndOfWeek.startTime).add(6, 'days').toDate(),
    });
  };

  const handleChangeWeek = (days?: number) => () => {
    let startOfWeek = siteTime.toStartEndOfWeekPart()?.startTime;

    if (days) startOfWeek = moment(week?.startDate).add(days, 'days').toDate();

    const startEndOfWeek = siteTime.toStartEndOfWeekPart(startOfWeek);

    setWeek({
      weekNumber: startEndOfWeek.weekNumber,
      startDate: startEndOfWeek.startTime,
      endDate: moment(startEndOfWeek.startTime).add(6, 'days').toDate(),
    });
  };

  useEffect(() => {
    if (!siteConfig) return;

    const startEndOfWeek = siteTime.toStartEndOfWeekPart();
    const reportTypes = siteConfig.config?.inspectionConfig?.inspectionReports?.modules?.flatMap(_ => _.reportTypes);

    // The furthest back we can go back would be 1 year in the past.
    // const retentionWeeks = maxNumber([...map(reportTypes, _ => _?.retentionWeeks), 1]);
    // const pastMaxRetentionByWeek = moment(startEndOfWeek.startTime).subtract(retentionWeeks, 'weeks');
    // Get the date 1 year before that (still aligned with week start)
    const pastYearStart = moment(startEndOfWeek.startTime).subtract(52, 'weeks');
    // verify buttons should only be listed for weeks within the past 4 weeks.
    const minAllowedEdit = moment(startEndOfWeek.startTime).subtract(4, 'weeks').startOf('day').toDate();

    setCalendarProps({
      minAllowedEdit,
      max: moment(startEndOfWeek.startTime).add(6, 'days').startOf('day').toDate(),
      min: pastYearStart.startOf('day').toDate(),
    });

    // This is to force reset the date
    setWeek({
      weekNumber: startEndOfWeek.weekNumber,
      startDate: startEndOfWeek.startTime,
      endDate: moment(startEndOfWeek.startTime).add(6, 'days').toDate(),
    });

    useSimpleSite.getState().setSite({
      id: siteConfig.id,
      name: siteConfig.name,
      marketId: siteConfig.config?.marketId,
      address: siteConfig.address,
    });
  }, [siteConfig?.id]);

  useEffect(() => {
    const name = profile ? [profile.userName, profile.firstName, profile.lastName].filter(Boolean).join('$') : undefined;
    setGeneralManager(isAuthorized ? { userId: profile?.userId, name: name } : undefined);
  }, [profile?.userId]);

  return (
    <ReviewVerifyReportsProvider siteId={siteConfig.id} userId={generalManager?.userId} username={generalManager?.name}>
      <VStack h="full">
        {!!tabItems.length && (
          <HStack alignItems="center" justifyContent="space-between" py={2} px={4} minH="58px">
            <HStack space={1.5} alignItems="center" justifyContent="space-between">
              <Button
                variant="unstyled"
                w={10}
                isDisabled={!min || !week?.startDate || formatToISOString(week.startDate, true) === formatToISOString(min, true)}
                onPress={handleChangeWeek(-7)}
              >
                <ArrowBackIcon size={4} color="gray.700" />
              </Button>

              <DateTimePopover
                key={`date-picker-${week?.startDate?.getTime()}`}
                mode="week"
                max={max}
                min={min}
                selectedDate={week?.startDate}
                onChange={handleSelectWeek}
              >
                <Button
                  key={`current-week[${locale}]`}
                  variant="unstyled"
                  startIcon={<FeatherIcons name="calendar" size={18} color="gray.700" />}
                >
                  {week
                    ? `Wk ${week.weekNumber} (${range(2)
                        .map(d =>
                          moment(week.startDate)
                            .add(6 * d, 'day')
                            .format('MMM DD'),
                        )
                        .join(' - ')})`
                    : ''}
                </Button>
              </DateTimePopover>

              <Button
                isDisabled={!max || !week?.endDate || formatToISOString(week.endDate, true) === formatToISOString(max, true)}
                variant="unstyled"
                w={10}
                onPress={handleChangeWeek(7)}
              >
                <ArrowForwardIcon size={4} color="gray.700" />
              </Button>

              <Button
                display={{ base: 'none', xl: 'flex' }}
                variant="unstyled"
                onPress={handleChangeWeek()}
                disabled={moment(week?.endDate).toDate() >= siteTime.today()}
              >
                {t('app:current_week')}
              </Button>
            </HStack>

            <HStack space={2} alignItems="center">
              <PopoverTooltipStoreReports />

              <HStack space={2} justifyContent="flex-end">
                <ButtonGroup key={locale} value={tabFocus.module} onChange={setModuleType}>
                  {tabItems.map(_tab => (
                    <ButtonGroup.Item key={_tab.module} alignItems="center" value={_tab.module} minW={90}>
                      {translate(_tab.title, _tab.titleTranslations)}
                    </ButtonGroup.Item>
                  ))}
                </ButtonGroup>
              </HStack>
            </HStack>
          </HStack>
        )}

        <ResourceLoader
          flex={1}
          isEmpty={tabItems.length === 0}
          emptyMessage={
            <Box alignItems="center" justifyContent="center" p={3} h="full" textAlign="center">
              <Text size="lg" fontWeight={600} color="gray.500" textAlign="center">
                {t('app:nothing_to_display')}.{`\n`}
                <Text size="md" fontWeight={400} color="gray.500">
                  {t('app:module_not_setup')}.
                </Text>
              </Text>
            </Box>
          }
        >
          {!!tabFocus.module && !!week?.startDate && (
            <StoreReports
              key={`${tabFocus.module === 1 ? 'food-safety-reports' : 'checklists'}.${
                siteConfig?.id ?? 'unknown'
              }.${week.startDate.getTime()}`}
              icon={tabFocus.icon}
              marketId={siteConfig?.config?.marketId}
              module={tabFocus.module}
              title={tabFocus.title}
              titleTranslations={tabFocus.titleTranslations}
              isEditEnabled={isEditEnabled && isAuthorized}
              generalManager={generalManager}
              week={week}
              onRegisterDownload={fn => setDownload(() => fn)}
            />
          )}
        </ResourceLoader>
      </VStack>
    </ReviewVerifyReportsProvider>
  );
};
